import { ReactElement, createContext, useContext, useEffect, useState } from 'react';
import { useLocations } from '../use-locations';
import { usePubnub } from '../use-pubnub/use-pubnub';

export const commTypes = ['Phone', 'SMS', 'Email'] as const;
export const deliveryTypes = ['Automated', 'Direct'] as const;
export type CommType = typeof commTypes[number];
export type DeliveryType = typeof deliveryTypes[number];

export interface ICommsFilter {
  commType?: CommType;
  deliveryType?: DeliveryType;
}

interface ICommsFilterContext {
  commsFilter: ICommsFilter;
  updateCommsFilter: (newFilter: Partial<ICommsFilter>) => Promise<void>;
}

const CommsFilterContext = createContext<ICommsFilterContext | null>(null);

export const useCommsFilter = (): ICommsFilterContext => {
  const context = useContext(CommsFilterContext);

  if (!context) throw new Error('useCommsFilter must be used within an CommsFilterContext');

  return context;
};

export function CommsFilterProvider({ children }: React.PropsWithChildren<{}>): ReactElement | null {
  const { mailboxes } = usePubnub();
  const { selectedLocation } = useLocations();
  const [commsFilter, setCommsFilter] = useState<ICommsFilter>({});

  useEffect(() => {
    if (!selectedLocation) return;

    setCommsFilter((prevFilter) => ({
      ...prevFilter,
      mailboxes: mailboxes.filter((m) => m.location?.id == selectedLocation?.id)
    }));
  }, [mailboxes, selectedLocation]);

  const updateCommsFilter = async (newFilter: Partial<ICommsFilter>): Promise<void> => {
    setCommsFilter((prevFilter) => ({
      ...prevFilter,
      ...newFilter
    }));
  };

  return (
    <CommsFilterContext.Provider value={{ commsFilter, updateCommsFilter }}>{children}</CommsFilterContext.Provider>
  );
}

export default useCommsFilter;
